import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import * as fromGroups from '../reducers/groups.reducer';
import * as fromRoot from '@app/reducers';
import { Group } from '@app/shared/models/group';
import { Member } from '@app/shared/models/member';
import { Role } from '@app/shared/models/role';
import { Team } from '@app/shared/models/team';
import { User } from '@app/shared/models/user';
import { DialogService } from '@ngneat/dialog';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { concatMap, filter, first, map, mergeMap, take, withLatestFrom } from 'rxjs/operators';
import { GroupsActions } from '../actions';
import { ModalAttachMemberComponent } from '../modals/modal-attach-member/modal-attach-member.component';
import { ModalDetachMemberComponent } from '../modals/modal-detach-member/modal-detach-member.component';
import { ModalAttachRoleComponent } from '../modals/modal-attach-role/modal-attach-role.component';
import { ModalDetachRoleComponent } from '../modals/modal-detach-role/modal-detach-role.component';
import { Identity } from '@app/shared/models/identity';


@Component({
  selector: 'app-group-details',
  templateUrl: './group-details.component.html',
  styleUrls: ['./group-details.component.scss']
})
export class GroupDetailsComponent implements OnInit, OnDestroy {

  teams$: Observable<Team[]>;
  roles$: Observable<Role[]>;
  identity$: Observable<Identity>;

  groupDetails$: Observable<Group>;
  groupDetailsLoading$: Observable<boolean>;
  groupDetailsLoaded$: Observable<boolean>;

  groupRoles$: Observable<Role[]>;
  groupRolesLoading$: Observable<boolean>;
  groupRolesLoaded$: Observable<boolean>;

  attachRoleToGroupPending$: Observable<boolean>;
  attachRoleToGroupSuccess$: Observable<boolean>;

  teamRoles$: Observable<Role[]>;
  teamRolesLoading$: Observable<boolean>;
  teamRolesLoaded$: Observable<boolean>;

  groupMembers$: Observable<User[]>;
  groupMembersLoading$: Observable<boolean>;
  groupMembersLoaded$: Observable<boolean>;

  accountMembers$: Observable<User[]>;
  accountMembersLoading$: Observable<boolean>;
  accountMembersLoaded$: Observable<boolean>;

  groupUpdating$: Observable<boolean>;
  groupUpdated$: Observable<boolean>;
  groupUpdatingError$: Observable<any>;

  groupUpdated = false;
  groupRoleUpdated = false;

  subscriptions: Subscription[] = [];
  roleListColumnsEnabled = [
    { id: 'translatedName', colName: 'PREMIUM_ADMIN_GROUPS_TABLE_ROLE_NAME', type: 'string', removable: false },
  ];
  membersListColumnsEnabled = [
    { id: 'lastName', colName: 'PREMIUM_ADMIN_GROUPS_TABLE_LAST_NAME', type: 'string', removable: false },
    { id: 'firstName', colName: 'PREMIUM_ADMIN_GROUPS_TABLE_FIRST_NAME', type: 'string', removable: false },
    { id: 'username', colName: 'PREMIUM_ADMIN_GROUPS_TABLE_LABEL_EMAIL', type: 'string', removable: false },
  ];
  selectedMemberToDetach: User;
  selectedRoleToDetach: any;

  groupDetailsForm: FormGroup;
  groupDetailsFormTouched = false;
  groupDetailsFormSubmitted = false;

  groupRoleForm: FormGroup;
  selectedRoleToAttach: Role;
  teamId: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store$: Store<fromRoot.State & fromGroups.State>,
    private readonly fb: FormBuilder,
    private dialog: DialogService,
  ) {
    this.route.params.pipe(mergeMap(params => {
      this.teamId = params.id;
      return [
        GroupsActions.LoadGroupMembers({ teamId: params.id, id: params.value }),
        GroupsActions.LoadAccountMembers(),
        GroupsActions.LoadGroupDetails({ teamId: params.id, id: params.value }),
        GroupsActions.LoadGroupRoles({ teamId: params.id, id: params.value }),
        GroupsActions.LoadAttributableRoles({ teamId: params.id }),
      ];
    })).subscribe(action => {
      this.store$.dispatch(action);
    });

    this.identity$ = this.store$.select(fromRoot.getActiveIdentity);

    this.groupDetails$ = this.store$.select(fromGroups.getGroupDetails);
    this.groupDetailsLoading$ = this.store$.select(fromGroups.getGroupDetailsLoading);
    this.groupDetailsLoaded$ = this.store$.select(fromGroups.getGroupDetailsLoaded);

    this.groupRoles$ = this.store$.select(fromGroups.getGroupRoles);
    this.groupRolesLoading$ = this.store$.select(fromGroups.getGroupRolesLoading);
    this.groupRolesLoaded$ = this.store$.select(fromGroups.getGroupRolesLoaded);

    this.attachRoleToGroupPending$ = this.store$.select(fromGroups.getAttachRoleToGroupPending);
    this.attachRoleToGroupSuccess$ = this.store$.select(fromGroups.getAttachRoleToGroupSuccess);

    this.teamRoles$ = this.store$.select(fromGroups.getTeamRoles);
    this.teamRolesLoading$ = this.store$.select(fromGroups.getTeamRolesLoading);
    this.teamRolesLoaded$ = this.store$.select(fromGroups.getTeamRolesLoaded);

    this.groupMembers$ = this.store$.select(fromGroups.getGroupMembers);
    this.groupMembersLoading$ = this.store$.select(fromGroups.getGroupMembersLoading);
    this.groupMembersLoaded$ = this.store$.select(fromGroups.getGroupMembersLoaded);

    this.accountMembers$ = this.store$.select(fromGroups.getAccountMembers);
    this.accountMembersLoading$ = this.store$.select(fromGroups.getAccountMembersLoading);
    this.accountMembersLoaded$ = this.store$.select(fromGroups.getAccountMembersLoaded);

    this.groupUpdating$ = this.store$.select(fromGroups.getGroupUpdating);
    this.groupUpdated$ = this.store$.select(fromGroups.getGroupUpdated);
    this.groupUpdatingError$ = this.store$.select(fromGroups.getGroupUpdatingError);

  }

  ngOnInit(): void {
    this.initGroupDetailsForm();
    this.initGroupRoleForm();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => {
      sub.unsubscribe();
    });
  }


  initGroupDetailsForm() {
    this.groupDetailsForm = this.fb.group({
      name: ['', Validators.required],
    });
  }

  initGroupRoleForm() {
    this.groupRoleForm = this.fb.group({
      role: [''],
    });
    this.groupRolesLoaded$.pipe(
      first(loaded => loaded),
      concatMap(() => this.groupRoles$.pipe(
        take(1),
        map((roles) => {
          if (roles.length) {
            this.selectedRoleToAttach = roles[0];
          }
        })
      ))
    );
  }

  get f() { return this.groupDetailsForm.controls; }

  async openModalAttachUserToGroup() {
    const group = await this.groupDetails$.pipe(first(groupDetails => !!groupDetails)).toPromise();
    const accountMembers = await this.accountMembers$.pipe(first(members => !!members)).toPromise();
    const groupMembers = await this.groupMembers$.pipe(first(members => !!members)).toPromise();
    const identity = await this.identity$.pipe(first(identity => !!identity)).toPromise();
    this.dialog.open(ModalAttachMemberComponent, {
      enableClose: false,
      closeButton: false,
      size: 'sm',
      data: {
        teamId: this.teamId,
        group,
        accountMembers,
        identity,
        groupMembers,
      }
    });
  }

  async openModalDetachUserFromGroup(member: Member) {
    const group = await this.groupDetails$.pipe(first(groupDetails => !!groupDetails)).toPromise();
    this.dialog.open(ModalDetachMemberComponent, {
      enableClose: true,
      closeButton: false,
      size: 'sm',
      data: {
        teamId: this.teamId,
        group,
        memberToDetach: member
      }
    });
  }

  async openModalAttachRoleToGroup() {
    const group = await this.groupDetails$.pipe(first(groupDetails => !!groupDetails)).toPromise();
    const attributableRoles = await this.groupRolesLoaded$.pipe(
      first(groupRolesLoaded => groupRolesLoaded),
      withLatestFrom(this.groupRoles$),
      concatMap(([groupRolesLoaded, groupRoles]) => this.teamRolesLoaded$.pipe(
        first(teamRolesloaded => teamRolesloaded),
        withLatestFrom(this.teamRoles$),
        map(([teamRolesloaded, teamRoles]) => teamRoles.filter(teamRole => !groupRoles.some(groupRole => groupRole.name === teamRole.name)))
      )))
      .toPromise();
    this.dialog.open(ModalAttachRoleComponent, {
      enableClose: false,
      closeButton: false,
      size: 'sm',
      data: {
        teamId: this.teamId,
        group,
        attributableRoles,
      }
    });
  }

  async openModalDetachRoleFromGroup(role: any) {
    const group = await this.groupDetails$.pipe(first(groupDetails => !!groupDetails)).toPromise();
    this.selectedRoleToDetach = role;
    this.dialog.open(ModalDetachRoleComponent, {
      enableClose: true,
      closeButton: false,
      size: 'sm',
      data: {
        teamId: this.teamId,
        group,
        roleToDetach: role
      }
    });
  }

  goToMemberDetails(userId: string) {
    this.route.params.pipe(take(1)).subscribe(({ id }) => {
      this.router.navigateByUrl('/account/team/' + id + '/members/' + userId);
    });
  }

  refreshDomains() {
    this.route.params.pipe(map(params =>
      GroupsActions.LoadGroupRoles({ teamId: params.id, id: params.value })
    )).subscribe(action => {
      this.store$.dispatch(action);
    });
  }

  refreshMembers() {
    this.route.params.pipe(map(params =>
      GroupsActions.LoadGroupMembers({ teamId: params.id, id: params.value })
    )).subscribe(action => {
      this.store$.dispatch(action);
    });
  }

  refreshRoles() {
    this.route.params.pipe(map(params =>
      GroupsActions.LoadGroupRoles({ teamId: params.id, id: params.value })
    )).subscribe(action => {
      this.store$.dispatch(action);
    });
  }

  back() {
    this.route.params.pipe(take(1)).subscribe(({ id }) => {
      this.router.navigateByUrl('/account/team/' + id + '/groups');
    });
  }

  onSubmit() {
    if (this.groupDetailsForm.valid) {
      this.route.params.pipe(
        map(params => GroupsActions.UpdateGroup({ teamId: params.id, group: { id: params.value, ...this.groupDetailsForm.value } })),
        take(1)
      ).subscribe(action => {
        this.store$.dispatch(action);
      });
      this.groupUpdated$.pipe(filter(updated => !!updated), take(1)).subscribe(updated => { if (updated) { this.groupUpdated = true; setTimeout(() => this.groupUpdated = false, 2000); } });
    }
  }


}
